/**
 * Shared, self-contained UI helpers for the "runs" resource family
 * (events, rule-runs, agent-runs, evidence, decisions) owned by Frontend D5.
 *
 * These intentionally depend only on pinned dependencies (`@refinedev/core`,
 * React) and the shadcn `ui` primitives + D2 `app` components imported by path.
 * They do NOT couple to un-pinned D1 `@/lib/*` export shapes, so the D5 files
 * compile and integrate cleanly regardless of D1 progress.
 */
import { useApiUrl } from "@refinedev/core";
import { useEffect, useState } from "react";

/** Format an ISO-8601 timestamp for display; passthrough for empty values. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/** Render `null`/`undefined`/empty strings as an em-dash placeholder. */
export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

/**
 * Pretty-print a value that may be a JSON string, a plain object, or scalar.
 * Returns a `{ value, isJson }` pair so callers can pick a JSON viewer vs text.
 */
export function parseMaybeJson(
  raw: string | null | undefined,
): { value: unknown; isJson: boolean } {
  if (raw === null || raw === undefined || raw === "") {
    return { value: null, isJson: false };
  }
  const trimmed = raw.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return { value: JSON.parse(trimmed), isJson: true };
    } catch {
      return { value: raw, isJson: false };
    }
  }
  return { value: raw, isJson: false };
}

/**
 * Resolve a stored artifact URL (a `r2://<key>` reference or a relative API
 * path) into a fetchable absolute URL against the worker API base.
 *
 * Convention (IMPLEMENTATION_CONTRACT §10): R2 objects are stored as
 * `r2://context-packets/{id}.json`, `r2://prompts/{id}.txt`,
 * `r2://outputs/{id}.json`. The worker exposes these through the storage
 * service; the portal fetches them via `${apiUrl}/storage/<key>`.
 */
export function resolveStorageUrl(
  apiUrl: string,
  stored: string | null | undefined,
): string | null {
  if (!stored) return null;
  if (stored.startsWith("http://") || stored.startsWith("https://")) {
    return stored;
  }
  if (stored.startsWith("r2://")) {
    const key = stored.slice("r2://".length);
    return `${apiUrl}/storage/${key}`;
  }
  if (stored.startsWith("/")) return stored;
  return `${apiUrl}/${stored}`;
}

export type RemoteContentState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; text: string }
  | { status: "error"; error: string };

/**
 * Fetch the text body of a stored artifact (context packet, prompt, output).
 * Uses the Refine API base URL + native fetch with credentials so the worker
 * session cookie is sent. Returns a small state machine for the UI.
 */
export function useRemoteContent(
  stored: string | null | undefined,
): RemoteContentState {
  const apiUrl = useApiUrl();
  const [state, setState] = useState<RemoteContentState>({ status: "idle" });

  useEffect(() => {
    if (!stored) {
      setState({ status: "idle" });
      return;
    }
    const url = resolveStorageUrl(apiUrl, stored);
    if (!url) {
      setState({ status: "idle" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetch(url, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setState({ status: "success", text });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            status: "error",
            error: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, stored]);

  return state;
}
